import { useState } from "react";

import { usePatchDocumentVersionMutation, useUploadFileMutation } from "@hooks";
import { Document, DocumentVersion } from "@models";
import { Box } from "@mui/material";
import { useIsDiffMode } from "src/components/DocViewer/components/hooks/useIsDiffMode";
import { FileDropZone } from "src/components/FileDropZone/FileDropZone";
import GenericModal from "src/components/GenericModal/GenericModal";

export type UploadModalType =
  | "NEW_CHANGES_DOC"
  | "EDIT_CHANGES_DOC"
  | "NEW_CHANGES_AND_NEW_DOC";

type UploadModalProps = {
  type: UploadModalType;
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  deviceId: string;
  selectedDoc: Document;
  selectedDocVersion: DocumentVersion;
  createNewVersion: (
    docId: string,
    file: File
  ) => Promise<DocumentVersion>;
  onVersionCreated?: (newVersion: DocumentVersion) => void;
};

/**
 * Modal for handling uploads of changes documents or new documents for the FileDocViewer
 */
export const UploadModal = ({
  type,
  isOpen,
  onClose,
  orgId,
  deviceId,
  selectedDoc,
  selectedDocVersion,
  createNewVersion,
  onVersionCreated,
}: UploadModalProps) => {
  const [documentVersionFile, setDocumentVersionFile] = useState<File | null>(
    null
  );
  const [changesFile, setChangesFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setIsDiffMode } = useIsDiffMode();

  const uploadFileMutation = useUploadFileMutation();
  const patchDocumentVersionMutation = usePatchDocumentVersionMutation();

  const isChangesDocModal =
    type === "NEW_CHANGES_DOC" || type === "EDIT_CHANGES_DOC";

  // Overwrite or add a changes file to a document version
  const attachChangesFile = async (changesFile: File, docVersionId: string) => {
    const uploadedFile = await uploadFileMutation.mutateAsync({
      orgId,
      file: changesFile,
      deviceId,
    });
    await patchDocumentVersionMutation.mutateAsync({
      orgId,
      docId: selectedDoc.id,
      deviceId,
      docVersionId,
      data: { changesFileId: uploadedFile.id },
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (isChangesDocModal && changesFile) {
        await attachChangesFile(changesFile, selectedDocVersion.id);
        setIsDiffMode(true);
      } else if (type === "NEW_CHANGES_AND_NEW_DOC" && documentVersionFile) {
        const newVersion = await createNewVersion(
          selectedDoc.id,
          documentVersionFile
        );

        if (changesFile) {
          await attachChangesFile(changesFile, newVersion.id);
        }

        onVersionCreated?.(newVersion);
      }
      setDocumentVersionFile(null);
      setChangesFile(null);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDocumentVersionFile(null);
    setChangesFile(null);
    onClose();
  };

  const isSubmitDisabled =
    isSubmitting || (isChangesDocModal ? !changesFile : !documentVersionFile);

  return (
    <GenericModal
      isOpen={isOpen}
      title={
        isChangesDocModal
          ? type === "NEW_CHANGES_DOC"
            ? "Upload Changes Document"
            : "Edit Changes Document"
          : "Upload New Version"
      }
      description={
        isChangesDocModal
          ? type === "NEW_CHANGES_DOC"
            ? "Add a document containing the redline changes between this and the previous version."
            : "Edit the document containing the redline changes between this and the previous version."
          : "Upload a new version of this document and optionally add a changes document containing the redline changes made between this and the previous version."
      }
      handleCancel={handleClose}
      handleSave={handleSubmit}
      saveText="Submit"
      isDisabled={isSubmitDisabled}
      isLoading={isSubmitting}
    >
      {isChangesDocModal ? (
        <Box className="flex flex-col gap-4 items-center">
          <FileDropZone
            file={changesFile}
            onFileChange={setChangesFile}
            label="Upload Changes Document"
          />
        </Box>
      ) : (
        <Box className="grid grid-cols-2 gap-4">
          <FileDropZone
            file={documentVersionFile}
            onFileChange={setDocumentVersionFile}
            label="Upload Document Version"
          />
          <FileDropZone
            file={changesFile}
            onFileChange={setChangesFile}
            label="Upload Changes Document (optional)"
            variant="secondary"
          />
        </Box>
      )}
    </GenericModal>
  );
};

export default UploadModal;
